import { api } from './client';
import type { Page } from './catalogs';

export type OrderKind = 'ORDER' | 'PROFORMA_INVOICE';

// ПРОДАЖ-розбивка Етап 1 (2026-06-03).
export type PaymentTermsType = 'PREPAY' | 'PARTIAL' | 'POSTPAY' | 'ON_DELIVERY';
export type ClosingStatus = 'OPEN' | 'CLOSED';

// ----- Customer order -----

export interface CustomerOrderItem {
  id?: string;
  line_number: number;
  /** 0445: рядок-ОЗ — nomenclature_id null, fixed_asset_id заданий. */
  nomenclature_id: string | null;
  fixed_asset_id?: string | null;
  characteristic_id?: string | null;
  quantity: string;
  quantity_shipped?: string;
  quantity_reserved?: string;
  price: string;
  discount_percent?: string;
  vat_rate_id: string;
  account_code?: string | null;
  amount_without_vat: string;
  vat_amount: string;
  total_amount: string;
  project_id?: string | null;
}

export interface CustomerOrderListItem {
  doc_kind?: 'GOODS' | 'SERVICE' | 'EQUIPMENT' | 'FIXED_ASSET' | null;
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  delivery_date: string | null;
  delivery_days?: number | null;
  prepayment_due_date?: string | null;
  reserve_until?: string | null;
  delivery_date_fixed?: boolean;
  state: string;
  order_kind: OrderKind;
  total_amount: string;
  posted: boolean;
  deletion_mark: boolean;
  project_id?: string | null;
  external_source?: string | null;
  // ПРОДАЖ-розбивка Етап 1.
  incoterms?: string | null;
  payment_terms_type?: PaymentTermsType | null;
  closing_status?: ClosingStatus;
  close_reason?: string | null;
  paid_amount?: string | null;   // ч.792: сплачено за замовленням (колонка/фільтр)
}

export interface CustomerOrder extends CustomerOrderListItem {
  // ч.1374: ознаки ПДВ як у 1С (умовчання — з картки організації).
  prices_include_vat: boolean;
  vat_considered: boolean;
  vat_auto: boolean;
  // П4a: сплачено за замовленням (GET single, Σ оплат з deal=замовлення).
  paid_amount?: string | null;
  contract_id: string;
  warehouse_id: string | null;
  amount_without_vat: string;
  vat_amount: string;
  posted_at: string | null;
  comment: string | null;
  items: CustomerOrderItem[];
  // ПРОДАЖ-розбивка Етап 1.
  prepayment_percent?: string | null;
  final_payment_days?: number | null;
  /** Computed: backend обчислює залежно від payment_terms_type. */
  due_now_amount?: string;
  closed_at?: string | null;
  closed_by?: string | null;
  close_reason?: string | null;
}

export interface CustomerOrderCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  /** 2026-06-02: nullable — backend auto-create default contract. */
  contract_id: string | null;
  warehouse_id?: string | null;
  delivery_date?: string | null;
  delivery_days?: number | null;
  prepayment_due_date?: string | null;
  reserve_until?: string | null;
  comment?: string | null;
  order_kind?: OrderKind;
  items: CustomerOrderItem[];
  // ПРОДАЖ-розбивка Етап 1.
  incoterms?: string | null;
  payment_terms_type?: PaymentTermsType | null;
  prepayment_percent?: string | null;
  final_payment_days?: number | null;
  // ч.1374: ознаки ПДВ як у 1С (умовчання — з картки організації).
  prices_include_vat?: boolean;
  vat_considered?: boolean;
  vat_auto?: boolean;
}

export type CustomerOrderUpdate = Partial<CustomerOrderCreate>;

export const customerOrders = {
  list: (params?: {
    organization_id?: string; contractor_id?: string;
    state?: string; posted?: boolean; order_kind?: OrderKind;
    closing_status?: ClosingStatus; doc_kind?: string;
    paid?: 'paid' | 'unpaid' | 'partial';
    limit?: number; offset?: number;
    sort_by?: string; sort_dir?: 'asc' | 'desc';
    date_from?: string;
    date_to?: string;
  }) =>
    api.get<Page<CustomerOrderListItem>>('/customer-orders', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<CustomerOrder>(`/customer-orders/${id}`).then((r) => r.data),
  create: (payload: CustomerOrderCreate) =>
    api.post<CustomerOrder>('/customer-orders', payload).then((r) => r.data),
  update: (id: string, payload: CustomerOrderUpdate) =>
    api.patch<CustomerOrder>(`/customer-orders/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/customer-orders/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<CustomerOrder>(`/customer-orders/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<CustomerOrder>(`/customer-orders/${id}/unpost`).then((r) => r.data),
  // ПРОДАЖ-розбивка Етап 5 (2026-06-03).
  close: (id: string, reason?: string) =>
    api.post<CustomerOrder>(
      `/customer-orders/${id}/close`,
      { reason: reason ?? null },
    ).then((r) => r.data),
  reopen: (id: string) =>
    api.post<CustomerOrder>(`/customer-orders/${id}/reopen`).then((r) => r.data),
};

// ----- Supplier order -----

export interface SupplierOrderItem {
  id?: string;
  line_number: number;
  nomenclature_id: string;
  characteristic_id?: string | null;
  quantity: string;
  quantity_received?: string;
  price: string;
  vat_rate_id: string;
  account_code?: string | null;
  amount_without_vat: string;
  vat_amount: string;
  total_amount: string;
  project_id?: string | null;
}

export interface SupplierOrderListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  delivery_date: string | null;
  delivery_days?: number | null;
  delivery_date_fixed?: boolean;
  state: string;
  order_kind: OrderKind;
  total_amount: string;
  posted: boolean;
  deletion_mark: boolean;
  project_id?: string | null;
  external_source?: string | null;
  // ПРОДАЖ-розбивка Етап 1.
  incoterms?: string | null;
  payment_terms_type?: PaymentTermsType | null;
  closing_status?: ClosingStatus;
  close_reason?: string | null;
  paid_amount?: string | null;   // ч.792: сплачено за замовленням (колонка/фільтр)
}

export interface SupplierOrder extends SupplierOrderListItem {
  // ч.1374: ознаки ПДВ як у 1С (умовчання — з картки організації).
  prices_include_vat: boolean;
  vat_considered: boolean;
  vat_auto: boolean;
  paid_amount?: string | null;
  contract_id: string;
  warehouse_id: string | null;
  amount_without_vat: string;
  vat_amount: string;
  posted_at: string | null;
  comment: string | null;
  items: SupplierOrderItem[];
  // ПРОДАЖ-розбивка Етап 1.
  prepayment_percent?: string | null;
  final_payment_days?: number | null;
  due_now_amount?: string;
  closed_at?: string | null;
  closed_by?: string | null;
  close_reason?: string | null;
}

export interface SupplierOrderCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  /** 2026-06-02: nullable — backend auto-create default contract. */
  contract_id: string | null;
  warehouse_id?: string | null;
  delivery_date?: string | null;
  delivery_days?: number | null;
  comment?: string | null;
  // ПРОДАЖ-розбивка Етап 1.
  incoterms?: string | null;
  payment_terms_type?: PaymentTermsType | null;
  prepayment_percent?: string | null;
  final_payment_days?: number | null;
  order_kind?: OrderKind;
  // ч.1374: ознаки ПДВ як у 1С (умовчання — з картки організації).
  prices_include_vat?: boolean;
  vat_considered?: boolean;
  vat_auto?: boolean;
  items: SupplierOrderItem[];
}

export type SupplierOrderUpdate = Partial<SupplierOrderCreate>;

export const supplierOrders = {
  list: (params?: {
    organization_id?: string; contractor_id?: string;
    state?: string; posted?: boolean; order_kind?: OrderKind;
    closing_status?: ClosingStatus;
    paid?: 'paid' | 'unpaid' | 'partial';
    limit?: number; offset?: number;
    sort_by?: string; sort_dir?: 'asc' | 'desc';
    date_from?: string;
    date_to?: string;
  }) =>
    api.get<Page<SupplierOrderListItem>>('/supplier-orders', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<SupplierOrder>(`/supplier-orders/${id}`).then((r) => r.data),
  create: (payload: SupplierOrderCreate) =>
    api.post<SupplierOrder>('/supplier-orders', payload).then((r) => r.data),
  update: (id: string, payload: SupplierOrderUpdate) =>
    api.patch<SupplierOrder>(`/supplier-orders/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/supplier-orders/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<SupplierOrder>(`/supplier-orders/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<SupplierOrder>(`/supplier-orders/${id}/unpost`).then((r) => r.data),
  // ПРОДАЖ-розбивка Етап 5 (2026-06-03).
  close: (id: string, reason?: string) =>
    api.post<SupplierOrder>(
      `/supplier-orders/${id}/close`,
      { reason: reason ?? null },
    ).then((r) => r.data),
  reopen: (id: string) =>
    api.post<SupplierOrder>(`/supplier-orders/${id}/reopen`).then((r) => r.data),
};
